import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';
import ReservationPhotos from './ReservationPhotos';
import './ReservationDetails.css';

function ReservationDetails() {
  const { id } = useParams();
  const { user, getAuthHeader } = useAuth();

  // Reservation state
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Sign-up sheet state
  const [signUpItems, setSignUpItems] = useState([]);
  const [newItem, setNewItem] = useState({ item: '', quantity: '', assignee: '' });
  const [showAddItem, setShowAddItem] = useState(false);
  const [addingItem, setAddingItem] = useState(false);

  // Members state
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const searchTimeout = useRef(null);

  // User groups state
  const [userGroups, setUserGroups] = useState([]);

  // Visibility state
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  // Payment state
  const [payments, setPayments] = useState([]);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    notes: ''
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  // Fetch reservation details
  const fetchReservation = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}`, {
        headers: getAuthHeader()
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Reservation not found');
        }
        if (response.status === 403) {
          throw new Error('You do not have access to this reservation');
        }
        throw new Error('Failed to load reservation');
      }

      const data = await response.json();
      setReservation(data);
    } catch (err) {
      setError(err.message);
    }
  }, [id, getAuthHeader]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/messages`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [id, getAuthHeader]);

  // Fetch sign-up items
  const fetchSignups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/signups`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setSignUpItems(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching signups:', err);
    }
  }, [id, getAuthHeader]);

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/reservation/${id}`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setPayments(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  }, [id, getAuthHeader]);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/members`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  }, [id, getAuthHeader]);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchReservation();
      await Promise.all([fetchMessages(), fetchSignups(), fetchPayments(), fetchMembers()]);
      setLoading(false);
    };

    loadData();
  }, [fetchReservation, fetchMessages, fetchSignups, fetchPayments, fetchMembers]);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    if (!reservation) return;

    const interval = setInterval(() => {
      fetchMessages();
      fetchSignups();
      fetchPayments();
      fetchMembers();
    }, 30000);

    return () => clearInterval(interval);
  }, [reservation, fetchMessages, fetchSignups, fetchPayments, fetchMembers]);

  // Fetch user groups for the group picker
  useEffect(() => {
    const fetchUserGroups = async () => {
      try {
        const response = await fetch(`${API_URL}/api/user-groups`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          setUserGroups(data.groups || []);
        }
      } catch (err) {
        console.error('Error fetching user groups:', err);
      }
    };
    fetchUserGroups();
  }, [getAuthHeader]);

  const handleAddFromGroup = async (groupId) => {
    if (!groupId) return;
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/members/from-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ groupId: parseInt(groupId) })
      });
      if (response.ok) {
        const data = await response.json();
        fetchMembers();
        if (data.added > 0) {
          alert(`Added ${data.added} member(s)${data.skipped > 0 ? `, ${data.skipped} skipped (already members)` : ''}`);
        } else {
          alert('All group members are already on this reservation.');
        }
      }
    } catch (err) {
      console.error('Error adding group to reservation:', err);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-pending', text: 'Pending Approval' },
      approved: { class: 'badge-approved', text: 'Confirmed' },
      rejected: { class: 'badge-rejected', text: 'Declined' }
    };
    return badges[status] || badges.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const calculateNights = (checkIn, checkOut) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  // Send a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ message: newMessage.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages([...messages, data.message]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Submit a payment
  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!newPayment.amount || submittingPayment) return;

    setSubmittingPayment(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/reservation/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(newPayment)
      });

      if (response.ok) {
        const data = await response.json();
        setPayments([data.payment, ...payments]);
        setNewPayment({
          amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: '',
          notes: ''
        });
        // Refresh reservation to get updated totals
        fetchReservation();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to submit payment');
      }
    } catch (err) {
      console.error('Error submitting payment:', err);
      alert('Failed to submit payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Add a sign-up item
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.item.trim() || addingItem) return;

    setAddingItem(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/signups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          item: newItem.item.trim(),
          quantity: newItem.quantity.trim() || '1',
          assignee: newItem.assignee.trim() || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSignUpItems([...signUpItems, data.item]);
        setNewItem({ item: '', quantity: '', assignee: '' });
        setShowAddItem(false);
      }
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setAddingItem(false);
    }
  };

  // Remove a sign-up item
  const handleRemoveItem = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/signups/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        setSignUpItems(signUpItems.filter(item => item.id !== itemId));
      }
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  // Volunteer for an item
  const handleVolunteer = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/signups/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ volunteer: true })
      });

      if (response.ok) {
        const data = await response.json();
        setSignUpItems(signUpItems.map(item =>
          item.id === itemId ? { ...item, assignee: data.item.assignee, assignee_id: data.item.assignee_id } : item
        ));
      }
    } catch (err) {
      console.error('Error volunteering:', err);
    }
  };

  // Unvolunteer from an item
  const handleUnvolunteer = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/signups/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ unvolunteer: true })
      });

      if (response.ok) {
        setSignUpItems(signUpItems.map(item =>
          item.id === itemId ? { ...item, assignee: null, assignee_id: null } : item
        ));
      }
    } catch (err) {
      console.error('Error unvolunteering:', err);
    }
  };

  // Toggle reservation visibility
  const handleToggleVisibility = async () => {
    if (updatingVisibility) return;

    setUpdatingVisibility(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/visibility`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ is_public: !reservation.is_public })
      });

      if (response.ok) {
        setReservation({ ...reservation, is_public: !reservation.is_public });
      }
    } catch (err) {
      console.error('Error updating visibility:', err);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  // Search users for member add
  const handleMemberSearch = (value) => {
    setMemberSearch(value);
    clearTimeout(searchTimeout.current);

    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(value.trim())}`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          // Filter out users already in members list and the reservation owner
          const existingIds = members.map(m => m.user_id);
          setSearchResults(data.filter(u =>
            !existingIds.includes(u.id) && u.email !== reservation?.email
          ));
        }
      } catch (err) {
        console.error('Error searching users:', err);
      }
    }, 300);
  };

  // Add member
  const handleAddMember = async (userId) => {
    if (addingMember) return;
    setAddingMember(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        setMemberSearch('');
        setSearchResults([]);
        fetchMembers();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to add member');
      }
    } catch (err) {
      console.error('Error adding member:', err);
    } finally {
      setAddingMember(false);
    }
  };

  // Remove member
  const handleRemoveMember = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}/members/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        setMembers(members.filter(m => m.user_id !== userId));
      }
    } catch (err) {
      console.error('Error removing member:', err);
    }
  };

  // Check if current user is the owner
  const isOwner = reservation?.email === user?.email;

  // Loading state
  if (loading) {
    return (
      <div className="reservation-details">
        <div className="loading-state">Loading reservation details...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="reservation-details">
        <div className="error-state">
          <p>{error}</p>
          <Link to="/my-reservations" className="back-link">← Back to My Reservations</Link>
        </div>
      </div>
    );
  }

  // No reservation found
  if (!reservation) {
    return (
      <div className="reservation-details">
        <div className="error-state">
          <p>Reservation not found</p>
          <Link to="/my-reservations" className="back-link">← Back to My Reservations</Link>
        </div>
      </div>
    );
  }

  const badge = getStatusBadge(reservation.status);
  const nights = calculateNights(reservation.check_in_date, reservation.check_out_date);

  return (
    <div className="reservation-details">
      <div className="details-header">
        <Link to="/my-reservations" className="back-link">
          ← Back to My Reservations
        </Link>
        <h2>{reservation.description || 'Reservation Details'}</h2>
        <span className="reservation-number">{reservation.reservation_number}</span>
      </div>

      <div className="details-layout">
        {/* Reservation Details Section */}
        <section className="details-section reservation-info">
          <h3>Reservation Details</h3>

          <div className="info-card">
            <div className="info-header">
              <span className={`status-badge ${badge.class}`}>{badge.text}</span>
            </div>

            <div className="dates-display">
              <div className="date-item">
                <label>Check-in</label>
                <span className="date-value">{formatDate(reservation.check_in_date)}</span>
              </div>
              <div className="date-arrow">→</div>
              <div className="date-item">
                <label>Check-out</label>
                <span className="date-value">{formatDate(reservation.check_out_date)}</span>
              </div>
            </div>

            <div className="info-grid">
              <div className="info-item">
                <label>Location</label>
                <span>Papa's Palace</span>
              </div>
              <div className="info-item">
                <label>Duration</label>
                <span>{nights} nights</span>
              </div>
              <div className="info-item">
                <label>Year</label>
                <span>{reservation.year || new Date(reservation.check_in_date).getFullYear()}</span>
              </div>
              {reservation.total_price > 0 && (
                <div className="info-item">
                  <label>Est. Price</label>
                  <span className="price">${reservation.total_price.toLocaleString()}</span>
                </div>
              )}
            </div>

            {reservation.notes && (
              <div className="notes-section">
                <label>Notes</label>
                <p>{reservation.notes}</p>
              </div>
            )}

            <div className="organizer-info">
              <label>Organized by</label>
              <span>{reservation.organizer} ({reservation.email})</span>
            </div>

            {/* Visibility toggle - only show to owner or admin */}
            {(isOwner || user?.role === 'admin') && (
              <div className="visibility-toggle">
                <label>Visibility</label>
                <div className="toggle-control">
                  <button
                    className={`toggle-btn ${reservation.is_public ? 'public' : 'private'}`}
                    onClick={handleToggleVisibility}
                    disabled={updatingVisibility}
                  >
                    {updatingVisibility ? 'Updating...' : reservation.is_public ? 'Public' : 'Private'}
                  </button>
                  <span className="toggle-hint">
                    {reservation.is_public
                      ? 'All users can see this reservation'
                      : 'Only you and admins can see this reservation'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Members Section */}
        <section className="details-section members-section">
          <h3>Members</h3>

          <div className="members-list">
            {members.length === 0 ? (
              <p className="no-members">No members added yet.</p>
            ) : (
              members.map(member => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <span className="member-name">{member.name}</span>
                    <span className="member-email">{member.email}</span>
                  </div>
                  {(isOwner || user?.role === 'admin' || member.user_id === user?.id) && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveMember(member.user_id)}
                      title="Remove member"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {(isOwner || user?.role === 'admin' || members.some(m => m.user_id === user?.id)) && (
            <>
            {userGroups.length > 0 && (
              <div className="group-picker">
                <select
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddFromGroup(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Add from group...</option>
                  {userGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name} ({g.member_count})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="member-search">
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => handleMemberSearch(e.target.value)}
                placeholder="Search users by name or email..."
                className="member-search-input"
                disabled={addingMember}
              />
              {searchResults.length > 0 && (
                <div className="search-results-dropdown">
                  {searchResults.map(u => (
                    <div
                      key={u.id}
                      className="search-result-item"
                      onClick={() => handleAddMember(u.id)}
                    >
                      <span className="result-name">{u.name}</span>
                      <span className="result-email">{u.email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>
          )}
        </section>

        {/* Payments Section - Show for approved reservations */}
        {reservation.status === 'approved' && (
          <section className="details-section payments-section">
            <h3>Payments</h3>

            {/* Payment Summary */}
            <div className="payment-summary">
              <div className="summary-item">
                <label>Total Price</label>
                <span className="summary-value">${reservation.total_price?.toLocaleString() || '0.00'}</span>
              </div>
              <div className="summary-item">
                <label>Verified Paid</label>
                <span className="summary-value amount-paid">${reservation.amount_paid?.toLocaleString() || '0.00'}</span>
              </div>
              {reservation.pending_payments_total > 0 && (
                <div className="summary-item">
                  <label>Pending Verification</label>
                  <span className="summary-value amount-pending">${reservation.pending_payments_total?.toLocaleString()}</span>
                </div>
              )}
              <div className="summary-item">
                <label>Amount Owed</label>
                <span className="summary-value amount-owed">${reservation.amount_owed?.toLocaleString() || '0.00'}</span>
              </div>
            </div>

            {/* Payment History */}
            <div className="payments-list-container">
              <h4>Payment History</h4>
              {payments.length === 0 ? (
                <p className="no-payments">No payments recorded yet.</p>
              ) : (
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Submitted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(payment => (
                      <tr key={payment.id} className={`payment-row ${payment.status || 'verified'}`}>
                        <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                        <td className="amount-cell">${parseFloat(payment.amount).toFixed(2)}</td>
                        <td>{payment.payment_method || '-'}</td>
                        <td>
                          <span className={`payment-status-badge status-${payment.status || 'verified'}`}>
                            {payment.status === 'verified' && 'Verified'}
                            {payment.status === 'pending' && 'Pending'}
                            {payment.status === 'rejected' && 'Rejected'}
                            {!payment.status && 'Verified'}
                          </span>
                        </td>
                        <td>{payment.created_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Submit Payment Form */}
            <div className="submit-payment-form">
              <h4>Submit a Payment</h4>
              <p className="form-hint">Submit a record of your payment. An admin will verify it.</p>
              <form onSubmit={handleSubmitPayment}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount ($)*</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={newPayment.amount}
                      onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                      placeholder="0.00"
                      required
                      disabled={submittingPayment}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Date*</label>
                    <input
                      type="date"
                      value={newPayment.payment_date}
                      onChange={(e) => setNewPayment({...newPayment, payment_date: e.target.value})}
                      required
                      disabled={submittingPayment}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Method</label>
                    <select
                      value={newPayment.payment_method}
                      onChange={(e) => setNewPayment({...newPayment, payment_method: e.target.value})}
                      disabled={submittingPayment}
                    >
                      <option value="">Select...</option>
                      <option value="Check">Check</option>
                      <option value="Cash">Cash</option>
                      <option value="Venmo">Venmo</option>
                      <option value="Zelle">Zelle</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-group full-width">
                  <label>Notes (optional)</label>
                  <input
                    type="text"
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                    placeholder="e.g., Check #1234, Venmo transaction ID"
                    disabled={submittingPayment}
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={submittingPayment || !newPayment.amount}>
                  {submittingPayment ? 'Submitting...' : 'Submit Payment'}
                </button>
              </form>
            </div>
          </section>
        )}

        {/* Photos Section */}
        <section className="details-section">
          <ReservationPhotos reservationId={reservation.id} />
        </section>

        {/* Chat/Discussion Section */}
        <section className="details-section chat-section">
          <h3>Discussion</h3>

          <div className="chat-container">
            <div className="messages-list">
              {messages.length === 0 ? (
                <div className="empty-messages">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div key={msg.id} className={`message ${msg.author === user?.name ? 'own-message' : ''}`}>
                    <div className="message-header">
                      <span className="message-author">{msg.author}</span>
                      <span className="message-time">{formatTimestamp(msg.timestamp)}</span>
                    </div>
                    <p className="message-text">{msg.text}</p>
                  </div>
                ))
              )}
            </div>

            <form className="message-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="message-input"
                disabled={sendingMessage}
              />
              <button type="submit" className="btn btn-primary" disabled={!newMessage.trim() || sendingMessage}>
                {sendingMessage ? 'Sending...' : 'Send'}
              </button>
            </form>
          </div>
        </section>

        {/* Sign-up Sheet Section */}
        <section className="details-section signup-section">
          <div className="section-header">
            <h3>Sign-up Sheet</h3>
            <button
              className="btn btn-success"
              onClick={() => setShowAddItem(!showAddItem)}
            >
              {showAddItem ? 'Cancel' : '+ Add Item'}
            </button>
          </div>

          {showAddItem && (
            <form className="add-item-form" onSubmit={handleAddItem}>
              <div className="form-row">
                <input
                  type="text"
                  value={newItem.item}
                  onChange={(e) => setNewItem({ ...newItem, item: e.target.value })}
                  placeholder="Item name (e.g., Snacks)"
                  className="item-input"
                  required
                  disabled={addingItem}
                />
                <input
                  type="text"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                  placeholder="Qty (e.g., 2 bags)"
                  className="quantity-input"
                  disabled={addingItem}
                />
                <input
                  type="text"
                  value={newItem.assignee}
                  onChange={(e) => setNewItem({ ...newItem, assignee: e.target.value })}
                  placeholder="Who's bringing it? (optional)"
                  className="assignee-input"
                  disabled={addingItem}
                />
                <button type="submit" className="btn btn-primary" disabled={addingItem}>
                  {addingItem ? 'Adding...' : 'Add'}
                </button>
              </div>
            </form>
          )}

          <div className="signup-table-container">
            <table className="signup-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Who's Bringing It</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {signUpItems.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="empty-table">
                      No items yet. Add something the group needs!
                    </td>
                  </tr>
                ) : (
                  signUpItems.map(item => (
                    <tr key={item.id} className={item.assignee ? 'assigned' : 'unassigned'}>
                      <td className="item-name">{item.item}</td>
                      <td className="item-quantity">{item.quantity}</td>
                      <td className="item-assignee">
                        {item.assignee ? (
                          <span className="assignee-badge">{item.assignee}</span>
                        ) : (
                          <span className="needs-volunteer">Needs volunteer</span>
                        )}
                      </td>
                      <td className="item-actions">
                        {!item.assignee ? (
                          <button
                            className="btn btn-success"
                            onClick={() => handleVolunteer(item.id)}
                          >
                            I'll bring it
                          </button>
                        ) : item.assignee === user?.name ? (
                          <button
                            className="btn btn-warning"
                            onClick={() => handleUnvolunteer(item.id)}
                          >
                            Remove me
                          </button>
                        ) : null}
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveItem(item.id)}
                          title="Remove item"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="signup-summary">
            <span className="summary-item">
              <strong>{signUpItems.filter(i => i.assignee).length}</strong> of <strong>{signUpItems.length}</strong> items claimed
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ReservationDetails;
